import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Shield, Users, BarChart3, Settings, Activity, LockKeyhole, Crown } from 'lucide-react';
import AdminDailyStats from '../../components/admin/AdminDailyStats';
import UserManagement from '../../components/admin/UserManagement';
import SubscriptionManagement from '../../components/admin/SubscriptionManagement';

const CardButton = ({ icon: Icon, title, value, description, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className="text-left group rounded-xl p-5 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-indigo-500 dark:hover:border-indigo-500 hover:shadow-lg transition-all duration-200"
    >
        <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                <Icon className="h-6 w-6" />
            </div>
            <div>
                <div className="text-gray-700 dark:text-gray-300 text-sm">{title}</div>
                <div className="text-2xl font-semibold text-gray-900 dark:text-white">{value}</div>
                {description && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{description}</div>
                )}
            </div>
        </div>
    </button>
);

const ActionTile = ({ icon: Icon, title, description, onClick, color = 'indigo' }) => (
    <button
        type="button"
        onClick={onClick}
        className="text-left flex flex-col gap-2 rounded-xl p-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md transition-all duration-200"
    >
        <div className={`inline-flex items-center gap-2 text-${color}-600 dark:text-${color}-400`}>
            <Icon className="h-5 w-5" />
            <span className="font-medium">{title}</span>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">{description}</div>
    </button>
);

const AdminDashboard = () => {
    const { currentUser } = useAuth();
    // 'overview' | 'dailyStats' | 'users' | 'subscriptions'
    const [view, setView] = useState('overview');

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                        <Shield className="h-8 w-8 text-indigo-600" /> Admin Dashboard
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-2">
                        Welcome {currentUser?.email} • Role: <span className="font-medium capitalize">{currentUser?.role}</span>
                    </p>
                </div>
                <Link
                    to="/dashboard"
                    className="px-4 py-2 rounded-lg bg-gray-900 text-white dark:bg-white dark:text-gray-900 hover:opacity-90 transition"
                >
                    Back to User Dashboard
                </Link>
            </div>

            {/* View switcher */}
            <div className="flex flex-wrap gap-2">
                <button
                    className={`px-3 py-1.5 rounded-md text-sm ${view === 'overview' ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}
                    onClick={() => setView('overview')}
                >
                    Overview
                </button>
                <button
                    className={`px-3 py-1.5 rounded-md text-sm ${view === 'dailyStats' ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}
                    onClick={() => setView('dailyStats')}
                >
                    Daily Stats
                </button>
                <button
                    className={`px-3 py-1.5 rounded-md text-sm ${view === 'users' ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}
                    onClick={() => setView('users')}
                >
                    User Management
                </button>
                <button
                    className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 ${view === 'subscriptions' ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}
                    onClick={() => setView('subscriptions')}
                >
                    <Crown className="h-4 w-4" />
                    Premium Subscriptions
                </button>
            </div>

            {view === 'overview' && (
                <>
                    {/* Quick stats */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <CardButton icon={Users} title="Active Users" value="—" description="Coming soon" onClick={() => setView('users')} />
                        <CardButton icon={BarChart3} title="Daily Stats" value="Open" description="Review daily performance" onClick={() => setView('dailyStats')} />
                        <CardButton icon={Crown} title="Premium Subscriptions" value="Approve" description="Manage premium subscriptions" onClick={() => setView('subscriptions')} />
                        <CardButton icon={Activity} title="System Health" value="OK" description="All services operational" onClick={() => setView('overview')} />
                    </div>

                    {/* Actions */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <ActionTile
                            icon={BarChart3}
                            title="View Daily Stats"
                            description="Analyze user performance and reset targets"
                            onClick={() => setView('dailyStats')}
                            color="indigo"
                        />
                        <ActionTile
                            icon={Crown}
                            title="Premium Subscriptions"
                            description="Approve user subscriptions"
                            onClick={() => setView('subscriptions')}
                            color="yellow"
                        />
                        <ActionTile
                            icon={Users}
                            title="User Management"
                            description="Manage users and roles"
                            onClick={() => setView('users')}
                            color="violet"
                        />
                        <ActionTile
                            icon={Settings}
                            title="Admin Settings"
                            description="Manage admin preferences (coming soon)"
                            onClick={() => setView('overview')}
                            color="emerald"
                        />
                    </div>

                    {/* Info card */}
                    <div className="rounded-xl p-5 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800">
                        <h2 className="text-lg font-semibold text-indigo-900 dark:text-indigo-200">Tips</h2>
                        <ul className="list-disc pl-5 mt-2 text-sm text-indigo-900/80 dark:text-indigo-200/80 space-y-1">
                            <li>Use Daily Stats to grant or revoke AI access per user.</li>
                            <li>Use Premium Subscriptions to manually approve user payments after verification.</li>
                            <li>Only users with role admin or super_admin can access this dashboard.</li>
                            <li>Use the theme toggle to switch between light and dark mode.</li>
                        </ul>
                    </div>
                </>
            )}

            {view === 'dailyStats' && (
                <div className="space-y-4">
                    <AdminDailyStats />
                </div>
            )}

            {view === 'users' && (
                <div className="space-y-4">
                    <UserManagement />
                </div>
            )}

            {view === 'subscriptions' && (
                <div className="space-y-4">
                    <SubscriptionManagement />
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;